import { useEffect, useState } from 'react';
import Loader from '../loader/loader.js';
import PreviewFileModal from '../preview-file-modal.js';
import { fetchSingleSentEmailData } from '../../database/fetch-sent-email-data';

function SentEmailsModal({ open, setOpen, docId }) {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({});
  const [openFilePreviewModal, setOpenFilePreviewModal] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState('');

  useEffect(() => {
    if (!docId) return;
    setIsLoading(true);
    const unsubscribe = fetchSingleSentEmailData(docId, (emailData) => {
      if (!emailData) return;
      setData({
        subject: emailData.subject || '',
        body: emailData.body || '',
        to: Array.isArray(emailData.to) ? emailData.to.join(', ') : (emailData.to || ''),
        from: emailData.from || '',
        dateTime: emailData.dateTime || '',
        attachments: Array.isArray(emailData.attachments) ? emailData.attachments : [],
        emailType: emailData.emailType || '',
      });
      setIsLoading(false);
    });
    return () => unsubscribe && unsubscribe();
  }, [docId]);

  if (isLoading) {
    return <Loader />;
  }

  const handleFilePreviewClick = (url) => {
    setPreviewFileUrl(url);
    setOpenFilePreviewModal(true);
  };

  return (
    <>
      <ModalContainer open={open} onClose={() => setOpen(false)} title={data.subject}>
        <style>{`
          .email-body-content table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 1rem;
          }
          .email-body-content th, .email-body-content td {
            border: 1px solid #d1d5db;
            padding: 8px;
            text-align: left;
          }
          .email-body-content th {
            background: #f3f4f6;
          }
          .email-body-content {
            overflow-x: auto;
          }
        `}</style>
        <div className="space-y-4 max-h-[calc(100vh-15rem)] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><DetailField id="to" label="To" value={data.to} /></div>
            
            <div><DetailField id="from" label="From" value={data.from} /></div>
            
            <div><DetailField id="dateTime" label="Date/Time" value={data.dateTime} /></div>
            
            <div><DetailField id="emailType" label="EmailType" value={data.emailType} /></div>
            
          </div>
          <div className="sm:col-span-2 email-body-content">
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Body</label>
            <div className="overflow-auto">
  <div
    className="block p-2.5 w-[100%] text-sm min-w-full email-body-content"
    dangerouslySetInnerHTML={{ __html: data.body }}
  />
</div>
          </div>
          <div className="sm:col-span-2">
            <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Attachments ({(data.attachments || []).length})</label>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {(data.attachments || []).map((attachment, index) => (
                <li key={index} className="py-3 flex items-center justify-between text-sm">
                  <span className="text-gray-900 dark:text-white">{attachment.name}</span>
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-500 dark:hover:text-blue-400"
                    onClick={() => handleFilePreviewClick(attachment.attachmentUrl)}
                  >
                    Preview
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </ModalContainer>
      <PreviewFileModal
        openModal={openFilePreviewModal}
        setOpenModal={setOpenFilePreviewModal}
        fileLink={previewFileUrl}
        attachments={data.attachments || []}
      />
    </>
  );
}

const ModalContainer = ({ open, onClose, title, children }) => (
  <div
    id="sentEmail-modal"
    tabIndex="-1"
    aria-hidden="true"
    className={`${
      open
        ? 'flex items-center justify-center overflow-y-auto overflow-x-hidden fixed inset-0 z-50 w-full h-full bg-black bg-opacity-50'
        : 'hidden'
    }`}
  >
    <div className="relative w-full max-w-[95vw] max-h-[90vh] p-4 overflow-auto">
      <div className="bg-white rounded-lg shadow dark:bg-gray-700">
        <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button
            type="button"
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
            onClick={onClose}
          >
            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
            </svg>
            <span className="sr-only">Close modal</span>
          </button>
        </div>
        <div className="p-4 md:p-5 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  </div>
);



const DetailField = ({ id, label, value }) => (
  <div className="sm:col-span-2">
    <div className="flex items-center justify-normal gap-2">
      <label
        htmlFor={id}
        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white w-28 min-w-[7rem]"
        style={{ minWidth: '7rem' }}
      >
        {label}
      </label>
      <input
        type="text"
        id={id}
        className="bg-gwhite border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-500 dark:focus:border-primary-500 flex-1"
        placeholder={label.toLowerCase()}
        defaultValue={value}
        readOnly
      />
    </div>
  </div>
);

export default SentEmailsModal; 